import { PersistentMessage } from "../sdk";
import { DocumentSchema, Message } from "../types/augmentations";

function messageRoleRank(role?: string): number {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "user") return 0;
  if (normalized === "assistant") return 1;
  if (normalized === "system") return 2;
  return 3;
}

export function compareMessagesForRender(a: Message, b: Message): number {
  const aCreated = new Date(a.created_at || 0).getTime();
  const bCreated = new Date(b.created_at || 0).getTime();
  if (aCreated !== bCreated) return aCreated - bCreated;

  const aEdited = new Date(a.edited_at || a.created_at || 0).getTime();
  const bEdited = new Date(b.edited_at || b.created_at || 0).getTime();
  if (aEdited !== bEdited) return aEdited - bEdited;

  const roleDiff = messageRoleRank(a.role) - messageRoleRank(b.role);
  if (roleDiff !== 0) return roleDiff;

  return String(a.id || "").localeCompare(String(b.id || ""));
}
function extractContent(pm: PersistentMessage): string {
  if (pm.content_text && pm.content_text.length > 0) {
    return pm.content_text;
  }

  if (pm.content && typeof pm.content === "string") {
    return pm.content;
  }

  if (pm.content && typeof pm.content === "object") {
    if (Object.keys(pm.content).length === 0) {
      return "";
    }

    const nested = (pm.content as Record<string, unknown>).content;
    if (typeof nested === "string") {
      return nested;
    }

    if (nested && typeof nested === "object") {
      return JSON.stringify(nested);
    }

    return "";
  }

  return "";
}

export function processPersistentMessage(
  pm: PersistentMessage
): Message | null {
  try {
    // Parse the content if it's a stringified JSON, otherwise use it directly if it's already a string
    const messageContent = extractContent(pm);

    // Handle documents properly by checking if 'results' key exists
    let documents: DocumentSchema[] | undefined = undefined;
    if (
      pm.documents &&
      typeof pm.documents === "object" &&
      "results" in pm.documents
    ) {
      // Asserting the type of pm.documents.results as an array of DocumentSchema
      const results = (pm.documents as { results: DocumentSchema[] }).results;
      documents = results.map(
        (doc): DocumentSchema => ({
          id: doc.id,
          title: doc.title,
          content: doc.content,
          metadata: doc.metadata,
          similarity: doc.similarity,
          file_id: doc.file_id,
          file_name: doc.file_name,
          project_id: doc.project_id,
        })
      );
    }

    const message: Message = {
      id: pm.id || "", // Assuming id is mandatory in Wayak AI Message
      content: messageContent,
      role: pm.role,
      citations: pm.citations || undefined,
      models: pm.models || undefined,
      documents: documents,
      intermediate_steps: pm.intermediate_steps || undefined,
      created_at: pm.created_at, // Add created_at
      edited_at: pm.edited_at, // Add edited_at
      thread_id: pm.thread_id, // Add thread_id
      message_processing_status: pm.message_processing_status,
    };
    return message;
  } catch (error) {
    console.error("Failed to convert message:", pm, error);
    return null;
  }
}

export function convertSinglePersistentMessageToMessage(
  persistentMessage: PersistentMessage
): Message | null {
  const message = processPersistentMessage(persistentMessage);
  return message ? message : null;
}

export function convertPersistentMessagesToWayakAIMessages(
  persistentMessages: PersistentMessage[]
): Message[] {
  return persistentMessages
    .map(processPersistentMessage)
    .filter((msg): msg is Message => msg !== null && msg.id !== "")
    .filter(
      (msg) =>
        msg.message_processing_status === "SUCCESS" ||
        msg.message_processing_status === "PROCESSING" ||
        msg.message_processing_status === "ERROR" ||
        msg.role === "system"
    )
    .sort(compareMessagesForRender);
}
